import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { visitorLotteryApi } from "./client";

const keys = {
  events: ["visitor-lottery-events"] as const,
  event: (id: string) => ["visitor-lottery-events", id] as const,
  registrations: (query: Record<string, unknown>) =>
    ["visitor-registrations", query] as const,
  report: (id: string) => ["visitor-lottery-report", id] as const,
};

export function useVisitorLotteryEventsQuery() {
  return useQuery({
    queryKey: keys.events,
    queryFn: () => visitorLotteryApi.listEvents(),
  });
}

export function useVisitorLotteryEventQuery(id: string | undefined) {
  return useQuery({
    queryKey: keys.event(id ?? ""),
    queryFn: () => visitorLotteryApi.getEvent(id!),
    enabled: !!id,
  });
}

export function useInitializeVisitorLotteryMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => visitorLotteryApi.initializeSummerVisitor2026(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.events });
    },
  });
}

export function useUpdateVisitorLotteryEventMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: Parameters<typeof visitorLotteryApi.updateEvent>[1];
    }) => visitorLotteryApi.updateEvent(id, body),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: keys.event(id) });
      qc.invalidateQueries({ queryKey: keys.events });
    },
  });
}

export function useCreateVisitorPrizeMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      eventId,
      body,
    }: {
      eventId: string;
      body: Parameters<typeof visitorLotteryApi.createPrize>[1];
    }) => visitorLotteryApi.createPrize(eventId, body),
    onSuccess: (_, { eventId }) => {
      qc.invalidateQueries({ queryKey: keys.event(eventId) });
    },
  });
}

export function useUpdateVisitorPrizeMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      eventId,
      prizeId,
      body,
    }: {
      eventId: string;
      prizeId: string;
      body: Parameters<typeof visitorLotteryApi.updatePrize>[2];
    }) => visitorLotteryApi.updatePrize(eventId, prizeId, body),
    onSuccess: (_, { eventId }) => {
      qc.invalidateQueries({ queryKey: keys.event(eventId) });
    },
  });
}

export function useDeleteVisitorPrizeMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      eventId,
      prizeId,
    }: {
      eventId: string;
      prizeId: string;
    }) => visitorLotteryApi.deletePrize(eventId, prizeId),
    onSuccess: (_, { eventId }) => {
      qc.invalidateQueries({ queryKey: keys.event(eventId) });
      qc.invalidateQueries({ queryKey: keys.report(eventId) });
    },
  });
}

export function useVisitorRegistrationsQuery(
  query: OffsetPageQueryDTO & {
    eventId?: string;
    prizeId?: string;
    redeemed?: string;
    won?: string;
    search?: string;
  },
) {
  return useQuery({
    queryKey: keys.registrations(query),
    queryFn: () => visitorLotteryApi.listRegistrations(query),
    enabled: !!query.eventId,
  });
}

export function useRedeemVisitorRegistrationMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => visitorLotteryApi.redeemRegistration(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["visitor-registrations"] });
      qc.invalidateQueries({ queryKey: keys.events });
    },
  });
}

export function useDeleteVisitorRegistrationMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => visitorLotteryApi.deleteRegistration(id),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ["visitor-registrations"] });
      qc.invalidateQueries({ queryKey: keys.events });
      if (result.registration.eventId) {
        qc.invalidateQueries({
          queryKey: keys.event(result.registration.eventId),
        });
        qc.invalidateQueries({
          queryKey: keys.report(result.registration.eventId),
        });
      }
    },
  });
}

export function useRedeemByCodeMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => visitorLotteryApi.redeemByCode(code),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["visitor-registrations"] });
      qc.invalidateQueries({ queryKey: keys.events });
    },
  });
}

export function useDeferredDrawMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      eventId,
      prizeId,
      count,
    }: {
      eventId: string;
      prizeId: string;
      count?: number;
    }) => visitorLotteryApi.drawDeferred(eventId, prizeId, count),
    onSuccess: (_, { eventId }) => {
      qc.invalidateQueries({ queryKey: keys.event(eventId) });
      qc.invalidateQueries({ queryKey: keys.report(eventId) });
      qc.invalidateQueries({ queryKey: ["visitor-registrations"] });
    },
  });
}

export function useVisitorLotteryReportQuery(eventId: string | undefined) {
  return useQuery({
    queryKey: keys.report(eventId ?? ""),
    queryFn: () => visitorLotteryApi.getReport(eventId!),
    enabled: !!eventId,
  });
}
